import { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Key, Save } from 'lucide-react'
import { toast } from 'sonner'
import type { ApiCredentialMetadata } from '@/lib/types'

export function ApiCredentials() {
  const [credentials, setCredentials] = useState<ApiCredentialMetadata[]>([])
  const [saving, setSaving] = useState(false)

  const [unipileKey, setUnipileKey] = useState('')
  const [unipileUrl, setUnipileUrl] = useState('https://api.unipile.com')

  useEffect(() => {
    loadCredentials()
  }, [])

  const loadCredentials = async () => {
    try {
      const response = await api.getCredentials()
      setCredentials(response.credentials)
    } catch (error) {
      console.error('Load credentials error:', error)
    }
  }

  const handleSaveUnipile = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!unipileKey.trim()) {
      toast.error('API key is required')
      return
    }

    try {
      setSaving(true)
      const response = await api.updateCredential({
        service: 'unipile',
        apiKey: unipileKey,
        baseUrl: unipileUrl,
      })

      toast.success(
        response.restart_recommended
          ? 'Saved to .env. Restart backend before running a new pipeline.'
          : 'Unipile credentials updated successfully'
      )
      setUnipileKey('')
      if (response.credentials) setCredentials(response.credentials)
      else loadCredentials()
    } catch (error: any) {
      toast.error(error.message || 'Failed to update credentials')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Key className="w-5 h-5 text-slate-400" />
        <h3 className="text-lg font-semibold">API Credentials</h3>
      </div>

      {/* Unipile Credentials */}
      <div className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
        <form onSubmit={handleSaveUnipile} className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-4">Unipile API</h4>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="unipile-key">API Key</Label>
                <Input
                  id="unipile-key"
                  type="password"
                  value={unipileKey}
                  onChange={(e) => setUnipileKey(e.target.value)}
                  placeholder="Enter Unipile API key"
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unipile-url">Base URL</Label>
                <Input
                  id="unipile-url"
                  value={unipileUrl}
                  onChange={(e) => setUnipileUrl(e.target.value)}
                  placeholder="https://api.unipile.com"
                  disabled={saving}
                />
              </div>

              <Button type="submit" disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Credentials'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Local .env Credentials List */}
      {credentials.length > 0 && (
        <div className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
          <h4 className="text-sm font-medium mb-1">Local .env Services</h4>
          <p className="mb-4 text-xs text-slate-500">
            Runtime credentials are loaded from the local .env file. After changing a key, restart the backend before new runs.
          </p>
          <div className="space-y-2">
            {credentials.map((cred) => (
              <div
                key={cred.service}
                className="grid gap-2 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 md:grid-cols-[1fr_auto]"
              >
                <div>
                  <span className="font-medium capitalize">{cred.service}</span>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                    <span>Key: <span className="font-mono text-slate-200">{cred.key_masked || 'configured'}</span></span>
                    {cred.key_fingerprint && <span>Fingerprint: <span className="font-mono">{cred.key_fingerprint}</span></span>}
                    {cred.base_url && <span>URL: <span className="font-mono">{cred.base_url}</span></span>}
                    {cred.model && <span>Model: <span className="font-mono">{cred.model}</span></span>}
                    {cred.source && <span>Source: <span className="font-mono">{cred.source}</span></span>}
                    <span>Updated {new Date(cred.updated_at).toLocaleDateString()}</span>
                    {cred.updated_by_name && <span>By {cred.updated_by_name}</span>}
                  </div>
                </div>
                <span className="self-start rounded border border-green-800 px-2 py-1 text-xs text-green-300">
                  Configured
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
